'use client';

import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';

import { isExternalLink } from '../../helpers/isExternalLink';
import type { Icon, SpaceProp } from '@rulebooks/evidend-titan';
import { withTitan, defaultStyles, textStyle, useTitanRouter } from '@rulebooks/evidend-titan';
import { spaceProp } from '../../mixins/spaceProp';

export type ButtonVariant = 'primary' | 'secondary' | 'clear' | 'danger' | 'white-bordered';
export type ButtonSize = 'S' | 'M' | 'L';

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'disabled'
> {
  onClick?: (e: React.MouseEvent) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  href?: string;
  iconLeft?: React.FunctionComponentElement<Icon>;
  iconRight?: React.FunctionComponentElement<Icon>;
  /**
   * @default 'primary'
   */
  variant?: ButtonVariant;
  /**
   * @default 'M'
   */
  size?: ButtonSize;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  /**
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset';
  spaceAfter?: SpaceProp;
  className?: string;
}

const StyledButton = styled.a.withConfig<
  ButtonProps & { rel?: string; target?: string; disabled?: boolean }
>({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop),
})`
  ${textStyle('label')};
  ${defaultStyles.fontSmoothing};
  ${defaultStyles.boxSizing};
  position: relative;
  outline: none;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  border: 0;
  line-height: 1.25;
  border-radius: 6px;
  font-weight: 600;
  transition:
    box-shadow 100ms ease-out,
    color 200ms ease-in-out,
    background-color 100ms ease-out,
    border-color 200ms ease-in-out;

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    pointer-events: none;
  }

  svg {
    flex: 0 0 auto;
  }

  ${({ theme }) =>
    switchProp('variant', {
      primary: css`
        background-color: ${theme.components.button.variants.primary.backgroundColor};
        color: ${theme.components.button.variants.primary.textColor};
        box-shadow: inset 0 0 0 1px ${theme.components.button.variants.primary.border};

        &:hover {
          background-color: ${theme.components.button.variants.primary.backgroundColorHover};
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.primary.borderHover},
            ${theme.components.button.boxShadowHover};
        }

        &:focus {
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.primary.borderFocus},
            inset 0 0 0 2px ${theme.components.button.variants.primary.borderFocusInset};
        }

        &:disabled {
          color: ${theme.components.button.variants.primary.textColorDisabled};
          background-color: ${theme.components.button.variants.primary.backgroundColorDisabled};
          box-shadow: inset 0 0 0 1px ${theme.components.button.variants.primary.borderDisabled};
        }
      `,
      secondary: css`
        background-color: ${theme.components.button.variants.secondary.backgroundColor};
        color: ${theme.components.button.variants.secondary.textColor};
        box-shadow: inset 0 0 0 1px ${theme.components.button.variants.secondary.border};

        &:hover {
          background-color: ${theme.components.button.variants.secondary.backgroundColorHover};
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.secondary.borderHover},
            ${theme.components.button.boxShadowHover};
        }

        &:focus {
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.secondary.borderFocus},
            inset 0 0 0 2px ${theme.components.button.variants.secondary.borderFocusInset};
        }

        &:disabled {
          color: ${theme.components.button.variants.secondary.textColorDisabled};
          background-color: ${theme.components.button.variants.secondary.backgroundColorDisabled};
          box-shadow: inset 0 0 0 1px ${theme.components.button.variants.secondary.borderDisabled};
        }
      `,
      clear: css`
        background-color: ${theme.components.button.variants.clear.backgroundColor};
        color: ${theme.components.button.variants.clear.textColor};
        text-decoration: ${theme.components.button.variants.clear.textDecoration};
        box-shadow: inset 0 0 0 1px ${theme.components.button.variants.clear.border};

        &:hover {
          color: ${theme.components.button.variants.clear.textColorHover};
          text-decoration: ${theme.components.button.variants.clear.textDecorationHover};
          box-shadow: inset 0 0 0 1px ${theme.components.button.variants.clear.borderHover};
        }

        &:focus {
          color: ${theme.components.button.variants.clear.textColorFocus};
          text-decoration: ${theme.components.button.variants.clear.textDecorationFocus};
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.clear.borderFocus},
            inset 0 0 0 1px ${theme.components.button.variants.clear.borderFocusInset};
        }

        &:disabled {
          text-decoration: ${theme.components.button.variants.clear.textDecoration};
          color: ${theme.components.button.variants.clear.textColorDisabled};
          background-color: ${theme.components.button.variants.clear.backgroundColorDisabled};
          box-shadow: inset 0 0 0 1px ${theme.components.button.variants.clear.borderDisabled};
        }
      `,
      danger: css`
        background-color: ${theme.components.button.variants.danger.backgroundColor};
        color: ${theme.components.button.variants.danger.textColor};
        box-shadow: inset 0 0 0 1px ${theme.components.button.variants.danger.border};

        &:hover {
          background-color: ${theme.components.button.variants.danger.backgroundColorHover};
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.danger.borderHover},
            ${theme.components.button.boxShadowHover};
        }

        &:focus {
          box-shadow:
            inset 0 0 0 1px ${theme.components.button.variants.danger.borderFocus},
            inset 0 0 0 2px ${theme.components.button.variants.danger.borderFocusInset};
        }

        &:disabled {
          color: ${theme.components.button.variants.danger.textColorDisabled};
          background-color: ${theme.components.button.variants.danger.backgroundColorDisabled};
          box-shadow: inset 0 0 0 1px ${theme.components.button.variants.danger.borderDisabled};
        }
      `,
      'white-bordered': css`
        background-color: #ffffff;
        color: #5e5e5e;
        border: 1px solid #8c8c8c;
        border-radius: 6px;

        &:hover {
          color: #121212;
          border-color: #747775;
        }
      `,
    })}

  ${({ theme }) =>
    switchProp('size', {
      S: css`
        padding: ${theme.components.button.sizes.S.padding};
      `,
      M: css`
        padding: ${theme.components.button.sizes.M.padding};
        height: 48px;
      `,
      L: css`
        padding: ${theme.components.button.sizes.L.padding};
        ${textStyle('labelLarge')};
      `,
    })}

  ${({ isFullWidth }) =>
    isFullWidth &&
    css`
      width: 100%;
      justify-content: center;
    `}

  ${({ spaceAfter }) => spaceProp(spaceAfter)}
`;

const ButtonText = styled.span<{ hasIconLeft: boolean; hasIconRight: boolean }>`
  ${({ hasIconLeft, theme }) =>
    hasIconLeft &&
    css`
      padding-left: ${theme.components.button.paddingIcon};
    `}
  ${({ hasIconRight, theme }) =>
    hasIconRight &&
    css`
      padding-right: ${theme.components.button.paddingIcon};
    `}
`;

const Button = withTitan<React.PropsWithChildren<ButtonProps>, HTMLAnchorElement>(
  (
    {
      href,
      iconLeft,
      iconRight,
      type = 'button',
      children,
      variant = 'primary',
      size = 'M',
      isDisabled,
      testId = 'button',
      ...rest
    },
    ref,
  ) => {
    const { linkWrapper } = useTitanRouter();

    const renderAs = href && !isDisabled ? 'a' : 'button';
    const rel = href && isExternalLink(href) ? 'noopener noreferrer' : undefined;
    const target = href && isExternalLink(href) ? '_blank' : undefined;

    const button = (
      <StyledButton
        ref={ref}
        as={renderAs}
        data-test-id={testId}
        disabled={isDisabled}
        href={href}
        rel={rel}
        size={size}
        target={target}
        type={href ? undefined : type}
        variant={variant}
        {...rest}
      >
        {iconLeft}
        {children && (
          <ButtonText hasIconLeft={!!iconLeft} hasIconRight={!!iconRight}>
            {children}
          </ButtonText>
        )}
        {iconRight}
      </StyledButton>
    );

    if (href) {
      return linkWrapper(button);
    }

    return button;
  },
  { name: 'Button' },
);

export { Button, StyledButton };
